"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type QuestionType =
  | "likert-5"
  | "likert-7"
  | "multiple-choice"
  | "yes-no"
  | "open-ended"
  | "demographic";

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: "likert-5", label: "Likert (5-point)" },
  { value: "likert-7", label: "Likert (7-point)" },
  { value: "multiple-choice", label: "Multiple choice" },
  { value: "yes-no", label: "Yes / No" },
  { value: "open-ended", label: "Open-ended" },
  { value: "demographic", label: "Demographic" },
];

const LIKERT_5_SCALE =
  "Scale: 1 = Strongly disagree, 2 = Disagree, 3 = Neither agree nor disagree, 4 = Agree, 5 = Strongly agree";

const LIKERT_7_SCALE =
  "Scale: 1 = Strongly disagree, 2 = Disagree, 3 = Somewhat disagree, 4 = Neither agree nor disagree, 5 = Somewhat agree, 6 = Agree, 7 = Strongly agree";

function likertStatements(topic: string): string[] {
  return [
    `I feel positively about ${topic}.`,
    `${capitalizeFirst(topic)} plays an important role in my daily life.`,
    `I am satisfied with my current experience of ${topic}.`,
    `I would recommend ${topic} to others in my situation.`,
    `I feel well informed about ${topic}.`,
  ];
}

const QUESTION_BUILDERS: Record<QuestionType, (topic: string) => string[]> = {
  "likert-5": likertStatements,
  "likert-7": likertStatements,
  "multiple-choice": (topic) => [
    `Which of the following best describes your experience with ${topic}? (a) [option] (b) [option] (c) [option] (d) Other — please specify`,
    `How often do you engage with ${topic}? (a) Daily (b) Weekly (c) Monthly (d) Rarely (e) Never`,
    `What is your main reason for using or engaging with ${topic}? (a) [reason] (b) [reason] (c) [reason] (d) Other — please specify`,
    `Which factor most influences your view of ${topic}? (a) [factor] (b) [factor] (c) [factor] (d) None of the above`,
  ],
  "yes-no": (topic) => [
    `Have you had direct experience with ${topic} in the past 12 months? (Yes / No)`,
    `Were you aware of ${topic} before taking this survey? (Yes / No)`,
    `Would you consider engaging with ${topic} in the future? (Yes / No / Unsure)`,
    `Has ${topic} affected a decision you made this year? (Yes / No)`,
  ],
  "open-ended": (topic) => [
    `In your own words, what does ${topic} mean to you?`,
    `Describe a recent experience you have had with ${topic}.`,
    `What would you change about ${topic}, and why?`,
    `What do you see as the biggest benefit and the biggest drawback of ${topic}?`,
  ],
  demographic: () => [
    "What is your age group? (a) 18-24 (b) 25-34 (c) 35-44 (d) 45-54 (e) 55-64 (f) 65+ (g) Prefer not to say",
    "What is your gender? (a) [option] (b) [option] (c) Self-describe: ___ (d) Prefer not to say",
    "What is the highest level of education you have completed? (a) High school (b) Some college (c) Bachelor's degree (d) Graduate degree (e) Other (f) Prefer not to say",
    "Which best describes your current occupation status? (a) Employed full-time (b) Employed part-time (c) Student (d) Not currently employed (e) Retired (f) Prefer not to say",
  ],
};

function capitalizeFirst(text: string): string {
  if (text.length === 0) {
    return text;
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function scaleTextFor(type: QuestionType): string | null {
  if (type === "likert-5") {
    return LIKERT_5_SCALE;
  }
  if (type === "likert-7") {
    return LIKERT_7_SCALE;
  }
  return null;
}

/** English strings. A translated content file overrides these through `ui`. */
export const SURVEY_QUESTION_GENERATOR_STRINGS = {
  questionType: "Question type",
  questionTypeOptions: {
    "likert-5": "Likert (5-point)",
    "likert-7": "Likert (7-point)",
    "multiple-choice": "Multiple choice",
    "yes-no": "Yes / No",
    "open-ended": "Open-ended",
    demographic: "Demographic",
  },
  topicLabel: "Topic or construct you are measuring",
  topicNotNeeded: " (not needed for demographics)",
  topicPlaceholder: "e.g. remote learning, campus dining, job satisfaction",
  generateQuestions: "Generate Questions",
  resultsHeading:
    "Template questions — adapt the wording and replace bracketed placeholders with real options",
  copy: "Copy",
  copied: "Copied!",
} as const;

export default function SurveyQuestionGeneratorWidget() {
  const [topic, setTopic] = useState("");
  const [questionType, setQuestionType] = useState<QuestionType>("likert-5");
  const [questions, setQuestions] = useState<string[]>([]);
  const [generatedType, setGeneratedType] = useState<QuestionType>("likert-5");
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const strings = useToolUi(SURVEY_QUESTION_GENERATOR_STRINGS);

  const needsTopic = questionType !== "demographic";
  const canGenerate = !needsTopic || topic.trim().length > 0;

  function handleGenerate() {
    if (!canGenerate) {
      return;
    }
    setQuestions(QUESTION_BUILDERS[questionType](topic.trim().toLowerCase()));
    setGeneratedType(questionType);
    setCopiedIndex(null);
  }

  function handleCopy(question: string, index: number) {
    const scale = scaleTextFor(generatedType);
    const text = scale ? `${question}\n${scale}` : question;
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  const scaleText = scaleTextFor(generatedType);

  return (
    <>
      {/* Question type */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.questionType}
        </p>
        <div className="flex flex-wrap gap-3">
          {QUESTION_TYPES.map((t) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={t.value}
            >
              <input
                checked={questionType === t.value}
                className="accent-primary"
                name="question-type"
                onChange={() => setQuestionType(t.value)}
                type="radio"
                value={t.value}
              />
              {strings.questionTypeOptions[t.value]}
            </label>
          ))}
        </div>
      </section>
      {/* Topic */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="survey-topic"
        >
          {strings.topicLabel}
          {needsTopic ? "" : strings.topicNotNeeded}
        </label>
        <input
          className="w-full rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
          disabled={!needsTopic}
          id="survey-topic"
          onChange={(e) => setTopic(e.target.value)}
          placeholder={strings.topicPlaceholder}
          type="text"
          value={topic}
        />
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={!canGenerate}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateQuestions}
        </button>
      </div>
      {/* Results */}
      {questions.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-medium text-foreground text-sm">
            {strings.resultsHeading}
          </h2>
          {scaleText && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-muted-foreground text-sm leading-relaxed">
                {scaleText}
              </p>
            </div>
          )}
          <ul className="space-y-3">
            {questions.map((question, index) => (
              <li
                className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-5"
                key={question}
              >
                <p className="text-foreground text-sm leading-relaxed">
                  {question}
                </p>
                <button
                  className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 font-medium text-foreground text-xs transition-colors hover:bg-muted"
                  onClick={() => handleCopy(question, index)}
                  type="button"
                >
                  {copiedIndex === index ? strings.copied : strings.copy}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
